/* ============================================================
   JUCE 3.0 · theme-account.js - the customer account area.

   Sign-in, register, the dashboard's returns, recent scents and credit
   panel. Their hooks exist only on customers/* templates, so this file is
   imported only when one of them is on the page and modulepreloaded by the
   layout on those templates. initAcctAuth and initAcctReturns keep their
   critical-tier place (they own controls); initAcctRecent and initAcctCredit
   keep their idle one. initAcctDisclose stays in theme.js, because the
   #hash it answers can point at any <details> on any page.

   HOW IT REACHES THEME.JS. The code below is theme.js's own, moved as it
   stood. What it still uses from theme.js arrives through mount(): the
   `let` list is filled once, from the kit theme.js hands over (chunkKit
   there), before any controller runs.
   Nothing here imports theme.js by URL: a URL without the ?v= digest
   would be a second copy of theme.js, booted twice.
   ============================================================ */
let afterDomReady,
  bagMoney,
  creditCall,
  creditCfg,
  creditMoney,
  juceIndex,
  laneStep,
  onIdle,
  reduceMotion,
  whenActive;

export function mount(k) {
  ({
    afterDomReady,
    bagMoney,
    creditCall,
    creditCfg,
    creditMoney,
    juceIndex,
    laneStep,
    onIdle,
    reduceMotion,
    whenActive
  } = k);
  return { initAcctAuth, initAcctReturns, initAcctRecent, initAcctCredit };
}

/* ---- Account dashboard: the recently-viewed shelf. Reads the same
   juce:recent:v1 trail the search popup writes and resolves it against
   the shared catalogue index. No hook, no index, no shelf. ---- */
function initAcctRecent() {
  const box = document.querySelector('[data-acct-recent]');
  if (!box) return;
  const lane = box.querySelector('[data-acct-recent-lane]');
  if (!lane) return;
  let handles = [];
  try { handles = JSON.parse(localStorage.getItem('juce:recent:v1') || '[]'); }
  catch (e) { return; }
  if (!Array.isArray(handles) || !handles.length) return;
  juceIndex().then((prep) => {
    if (!prep) return;
    const frag = document.createDocumentFragment();
    handles.slice(0, 8).forEach((h) => {
      const p = prep.byHandle[h];
      if (!p) return;
      const a = document.createElement('a');
      a.className = 'acct-chip';
      a.href = p.u;
      /* THE BOTTLE, NOT THE LIFESTYLE SHOT. `img` on a row is whatever the
         merchant set as custom.main_product_image, and on this catalogue that
         is a 3413x2160 landscape file on 40 of 42 products - cropped into a
         portrait tile it becomes an unreadable slice of a wide photograph.
         `pimg` is the packshot the same row resolved before that override:
         the sellable variant's own square photo. Fall back to img so a
         product with no variant photograph still shows something. */
      const src = p.pimg || p.img;
      if (src) {
        const img = document.createElement('img');
        img.src = src; img.alt = ''; img.loading = 'lazy'; img.decoding = 'async';
        img.width = 240; img.height = 320;
        a.appendChild(img);
      }
      const name = document.createElement('span');
      name.className = 'acct-chip__name';
      name.textContent = p.t || h;
      a.appendChild(name);
      frag.appendChild(a);
    });
    if (!frag.childNodes.length) return;
    lane.replaceChildren(frag);
    box.hidden = false;

    /* ---- F4: arrow keys, and arrows that only exist when they do something.

       WIRED HERE RATHER THAN AT BOOT, and it is the one case where that is
       correct. The boot-tier rule is "does a tap on this do nothing if it has
       not run yet" - but until this line runs the section is `hidden` and the
       lane holds no tiles, so there is nothing on screen to press or step
       through. The control and its handler appear in the same frame.

       Mouse drag is NOT here: `data-lane` on the div is all initLaneDrag
       needs, it binds the container, and it runs at the critical tier - it is
       already live on this element, empty or not. Touch was always native. */
    lane.addEventListener('keydown', (e) => {
      /* Never steal a key from something inside the lane that wants it. The
         tiles are links, so Tab and Enter belong to them. */
      if (e.target !== lane) return;
      const step = laneStep(lane);
      const smooth = reduceMotion.matches ? 'auto' : 'smooth';
      let left = null;
      if (e.key === 'ArrowRight') left = lane.scrollLeft + step;
      else if (e.key === 'ArrowLeft') left = lane.scrollLeft - step;
      else if (e.key === 'Home') left = 0;
      else if (e.key === 'End') left = lane.scrollWidth;
      else return;
      e.preventDefault();                 /* or the PAGE scrolls as well */
      lane.scrollTo({ left: left, behavior: smooth });
    });

    /* The arrows ship `hidden`. Every other lane in this theme carries a
       dozen cards and can assume it overflows; this one carries between one
       and eight, so at three tiles on a desktop the strip fits and a pair of
       arrows would be two controls that do nothing when pressed. 4px of slop
       because scrollWidth and clientWidth are sub-pixel and a lane that fits
       exactly can report a 0.5px difference. */
    const arrows = Array.from(box.querySelectorAll('[data-acct-recent-arrow]'));
    if (arrows.length) {
      const sizeArrows = () => {
        const over = lane.scrollWidth - lane.clientWidth > 4;
        arrows.forEach((b) => { b.hidden = !over; });
      };
      sizeArrows();
      if (window.ResizeObserver) new ResizeObserver(sizeArrows).observe(lane);
      else window.addEventListener('resize', sizeArrows);
    }
  }).catch(() => { /* index unavailable - the shelf just stays away */ });
}

/* ============================================================
   The customer's returns, inside their account.  (G1, owner, 2026-08-25:
   "the customer can see them, cancel, or change their decision, fully
   compatible with our new RMA system".)

   WHEN: critical tier, and that is not about the fold. This owns two controls
   a shopper presses - Withdraw, and Change what happens - and the boot-tier
   rule in this file is "does a tap on this do nothing if it has not run yet".
   The one network read it does is held behind onIdle + whenActive INSIDE the
   function, exactly as initAcctAuth and initHomeReviews do, so sitting in the
   critical tier costs the page no network at all.

   HOW THE SHOPPER IS PROVEN. There is no authenticated client lane to JENNIE
   and this does not invent one - see the long note in snippets/acct-returns
   .liquid. It spends the same proof a stranger spends on /pages/returns, an
   order number plus one detail off that order, with the difference that a
   signed-in customer never types either: Liquid printed both into the
   attributes. The server still reads the order from Shopify and still checks
   the pair. Nothing is believed here that was not believed before.

   EVERY WORD ABOUT A RETURN COMES OFF THE WIRE. The state sentences are
   saying() in rma-public.ts and the method labels are GET /api/rma/options.
   Whether Cancel and Change may be offered at all is `canCancel` and
   `canChangeTo`, also from the server. Nothing about the state machine is
   re-derived in this file, because a second copy of that table in the theme is
   how the widget ended up saying "we are looking at it now" for eleven
   different states.
   ============================================================ */
function initAcctReturns() {
  const box = document.querySelector('[data-acct-returns]');
  if (!box) return;
  const list = box.querySelector('[data-rma-list]');
  const say = box.querySelector('[data-rma-say]');
  const api = String(box.dataset.rmaApi || '').trim().replace(/\/+$/, '');
  const preview = String(box.dataset.rmaPreview || '').trim();
  const contact = String(box.dataset.rmaContact || '').trim();
  const orders = String(box.dataset.rmaOrders || '').split('|')
    .map((s) => s.trim()).filter(Boolean);
  if (!list || !api || !contact || !orders.length) return;

  /* The order name the server accepted. Once one is proven every later call
     uses it and nothing else, so a refresh does not walk the whole list again
     and burn the per-order budget the panel needs in order to refresh. */
  let proven = null;
  let vocab = null;
  let rows = [];
  let busy = false;

  const rand = (cents) => 'R' + (Math.round(Number(cents || 0)) / 100)
    .toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  async function call(path, body, method) {
    const res = await fetch(api + path, {
      method: method || 'POST',
      headers: Object.assign(
        { 'Content-Type': 'application/json' },
        preview ? { 'x-rma-preview': preview } : {},
      ),
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const raw = await res.text();
    let data = {};
    try { data = raw ? JSON.parse(raw) : {}; } catch (e) { /* not JSON */ }
    if (!res.ok) {
      const err = new Error(data.error || ('Something went wrong (' + res.status + ').'));
      err.status = res.status;
      throw err;
    }
    return data;
  }

  function speak(msg) { if (say) say.textContent = msg || ''; }

  /* The served label for a method, never a typed one. Falls back to the raw
     word rather than to a guess: an unlabelled method is a dial that moved,
     and inventing "Store credit" here is exactly the drift /api/rma/options
     exists to stop. */
  function methodLabel(id) {
    const m = vocab && Array.isArray(vocab.methods)
      ? vocab.methods.filter((x) => x.id === id)[0] : null;
    return (m && m.label) || String(id || '').replace(/_/g, ' ');
  }

  function node(tag, cls, text) {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  /* HTTP AND HTTPS AND NOTHING ELSE. Every URL a card draws arrives over the
     wire - the courier's label, Shopify's invoice, the order status page -
     and none of them is typed by a customer, so this is a floor rather than a
     fix for a known hole. It is a cheap floor: `a.href = whatever` will
     happily accept `javascript:` and run it on the press, and a4_url and
     sticker_url are ordinary database columns that the walk-in counter and
     the courier integration both write. Anything that is not a plain web
     address is simply not drawn. */
  function link(href, label, cls) {
    const url = String(href || '');
    if (!/^https?:\/\//i.test(url)) return null;
    const a = document.createElement('a');
    a.href = url;
    a.className = cls || 'btn btn--secondary btn--sm';
    a.textContent = label;
    /* A courier label is a PDF on the courier's own host, and a payment link
       leaves for Shopify's checkout. Both open away from a page the shopper is
       in the middle of using. */
    a.target = '_blank';
    a.rel = 'noopener';
    return a;
  }

  /* append() ignores a null, so a refused URL simply leaves no button. */
  function addLink(parent, href, label, cls) {
    const a = link(href, label, cls);
    if (a) parent.append(a);
  }

  /* ---- the pair, spent once ---- */

  async function fetchMine() {
    const tries = proven ? [proven] : orders;
    for (let i = 0; i < tries.length; i++) {
      try {
        const out = await call('/api/rma/mine', { order: tries[i], contact: contact });
        proven = tries[i];
        return Array.isArray(out.returns) ? out.returns : [];
      } catch (err) {
        /* A 404 here is the ordinary case, not a failure: customer.email is
           the ACCOUNT address and order.email is what was typed at checkout,
           and on this shop they often differ. Try the next order.

           A 429 or a 503 is about the SERVER, not about this order, so trying
           five more orders would only dig the hole deeper. */
        if (err.status === 429 || err.status === 503) throw err;
      }
    }
    return null;
  }

  /* ---- one card ---- */

  function card(r) {
    const art = node('article', 'acct-rma-card');
    art.dataset.rmaId = r.rmaId || '';

    const head = node('div', 'acct-rma-card__head');
    head.append(node('span', 'acct-rma-card__id', r.rmaId || 'Return'));
    /* THE PILL REUSES .acct-tag, which the order list already uses two
       sections up. `done`, `needsYou` and `state` come off the wire, so the
       colour follows the server's own reading rather than a list of state
       names kept here.

       THREE MEANINGS, AND THE FIRST DRAFT HAD TWO OF THEM BACKWARDS. Amber is
       "there is something for YOU to do" and beats everything, because that is
       the only row a shopper has to act on. Green is "finished, and it went
       well" - settled and nothing else, or a withdrawn return would come out
       the same colour as a completed one. Everything in between is neutral:
       "Checking the photographs" was rendering GREEN, which reads as done. */
    const tone = r.needsYou ? ' acct-tag--wait'
      : (r.done && r.state === 'settled') ? ' acct-tag--ok'
        : '';
    head.append(node('span', 'acct-tag' + tone, r.headline || 'In progress'));
    art.append(head);

    art.append(node('p', 'acct-rma-card__detail', r.detail || ''));

    /* ---- the facts, as a description list ---- */
    const dl = node('dl', 'acct-rma-card__facts');
    const fact = (k, v) => {
      if (!v) return;
      dl.append(node('dt', null, k));
      dl.append(node('dd', null, v));
    };
    fact('Order', r.orderName);
    fact('You asked for', methodLabel(r.method));
    if (r.submittedAt) {
      const d = new Date(r.submittedAt);
      if (!isNaN(d)) {
        fact('Started', d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }));
      }
    }
    /* THE MONEY, AND ONLY WHEN THERE IS A NUMBER WORTH SAYING. balanceCents is
       what actually reaches somebody; creditCents on an exchange is what pays
       for the swap and is not a figure anyone receives, which is the exact lie
       the done screen used to tell. */
    /* A NEGATIVE BALANCE MEANS THEY OWE US - the same sign convention
       rma-money.ts writes and rma-mail.ts reads (`const owes =
       f.balanceCents < 0`). And once a return is settled the money has already
       moved, so "Coming back to you" would be a promise about the past. */
    if (Number(r.balanceCents) > 0) fact(r.done ? 'Settled for' : 'Coming back to you', rand(r.balanceCents));
    else if (Number(r.balanceCents) < 0) fact('Still to pay', rand(Math.abs(r.balanceCents)));
    if (Number(r.feeCents) > 0) fact('Return fee', rand(r.feeCents));
    if (dl.childNodes.length) art.append(dl);

    /* ---- where the parcel goes ----
       courierView() on the server is the same reader the acceptance email and
       the done screen use, so the locker named here and the locker named in
       the email cannot differ. Until this panel existed these three facts -
       the point, its PIN and the label - lived only in an email the shopper
       had to go and find. */
    const k = r.courier || null;
    if (k && k.method === 'drop_off' && k.dropOff) {
      const box2 = node('div', 'acct-rma-card__where');
      box2.append(node('span', 'acct-rma-card__wherek', 'Drop it off'));
      if (k.dropOff.name) box2.append(node('p', 'acct-rma-card__wherev', k.dropOff.name));
      if (k.dropOff.address) box2.append(node('p', 'acct-rma-card__wherec', k.dropOff.address));
      if (k.dropOff.pin) {
        const pin = node('p', 'acct-rma-card__pin');
        pin.append(node('span', 'acct-rma-card__pink', 'PIN'));
        pin.append(node('strong', null, String(k.dropOff.pin)));
        if (k.dropOff.expiry) pin.append(node('span', 'acct-rma-card__pine', 'until ' + k.dropOff.expiry));
        box2.append(pin);
      }
      art.append(box2);
    } else if (k && k.method === 'courier' && (k.when || k.address)) {
      const box2 = node('div', 'acct-rma-card__where');
      box2.append(node('span', 'acct-rma-card__wherek', 'We collect'));
      if (k.when) box2.append(node('p', 'acct-rma-card__wherev', k.when));
      if (k.address) box2.append(node('p', 'acct-rma-card__wherec', k.address));
      art.append(box2);
    }

    /* ---- what they can do about it ---- */
    const acts = node('div', 'acct-rma-card__acts');
    if (r.payUrl && Number(r.balanceCents) < 0) addLink(acts, r.payUrl, 'Settle the difference', 'btn btn--sm');
    if (r.trackUrl) addLink(acts, r.trackUrl, 'Track it', 'btn btn--secondary btn--sm');
    if (r.a4Url) addLink(acts, r.a4Url, 'Courier label', 'btn btn--ghost btn--sm');
    if (r.orderUrl) addLink(acts, r.orderUrl, 'The new order', 'btn btn--ghost btn--sm');

    /* CHANGE WHAT HAPPENS.

       `canChangeTo` is the server's list and it is deliberately short: the
       changes that need nothing typed. A different bottle needs the picker and
       cash to a card needs a bank account, so those two are a fresh request -
       said in those words below rather than offered as a button that would
       then ask for six more answers inside an account panel. */
    const can = Array.isArray(r.canChangeTo) ? r.canChangeTo : [];
    if (can.length) {
      const det = node('details', 'acct-rma-card__change');
      const sum = node('summary', 'btn btn--ghost btn--sm', 'Change what happens');
      det.append(sum);
      const body = node('div', 'acct-rma-card__changebody');
      can.forEach((id) => {
        const b = node('button', 'acct-rma-card__opt', 'Make it ' + methodLabel(id).toLowerCase());
        b.type = 'button';
        b.addEventListener('click', () => rethink(r, id));
        body.append(b);
      });
      body.append(node('p', 'acct-rma-card__note',
        'Want a different bottle instead, or the money back on a card? That one needs a few more '
        + 'details, so it is a fresh request rather than a change. Withdraw this one and start again '
        + 'and nothing is lost.'));
      det.append(body);
      acts.append(det);
    }

    if (r.canCancel) {
      const b = node('button', 'acct-rma-card__pull', 'Withdraw this return');
      b.type = 'button';
      b.addEventListener('click', () => withdraw(r));
      acts.append(b);
    } else if (r.cannotCancelWhy && !r.done) {
      art.append(node('p', 'acct-rma-card__note', r.cannotCancelWhy));
    }

    if (acts.childNodes.length) art.append(acts);
    return art;
  }

  /* ---- the two writes ---- */

  async function withdraw(r) {
    if (busy) return;
    /* ONE QUESTION, and it names the consequence rather than asking "are you
       sure". Withdrawing frees the order for a new return, which is the fact
       somebody who changed their mind actually needs. */
    if (!window.confirm('Withdraw return ' + r.rmaId + '? Nothing will come back to us, and the order '
      + 'is free for a new request straight away. We will email you to confirm.')) return;
    busy = true;
    speak('Withdrawing ' + r.rmaId + '...');
    try {
      await call('/api/rma/cancel', { rmaId: r.rmaId, email: contact });
      speak('Return ' + r.rmaId + ' is withdrawn. A confirmation is on its way to you by email.');
      await refresh();
    } catch (err) {
      speak(err.message);
    } finally {
      busy = false;
    }
  }

  async function rethink(r, want) {
    if (busy) return;
    busy = true;
    speak('Changing ' + r.rmaId + '...');
    try {
      const out = await call('/api/rma/rethink', { rmaId: r.rmaId, email: contact, method: want });
      speak('Return ' + r.rmaId + ' is now ' + methodLabel(out.method || want).toLowerCase() + '. '
        + 'The team has been told and the amount has been worked out again.');
      await refresh();
    } catch (err) {
      speak(err.message);
    } finally {
      busy = false;
    }
  }

  /* ---- drawing ---- */

  function draw() {
    const frag = document.createDocumentFragment();
    rows.forEach((r) => frag.append(card(r)));
    list.replaceChildren(frag);
    /* NO RETURNS, NO SECTION. A returns panel that says "you have no returns"
       is a heading, a rule and an apology on the account page of somebody who
       has never sent anything back. */
    box.hidden = rows.length === 0;
  }

  async function refresh() {
    const got = await fetchMine().catch((err) => { speak(err.message); return null; });
    if (got == null) { box.hidden = true; return; }
    rows = got;
    draw();
  }

  /* THE NETWORK IS HELD HERE, not at the boot tier. Same shape as
     initAcctAuth: the controls above are bound the moment this runs, and the
     one read waits for idle and for a document that is genuinely the
     shopper's rather than a prerender. */
  onIdle(() => whenActive(async () => {
    /* The vocabulary first and separately, because it is cached for five
       minutes on the server and a failure to read it must not lose the list -
       methodLabel() degrades to the raw word. */
    vocab = await call('/api/rma/options', null, 'GET').catch(() => null);
    await refresh();
  }));
}

/* ---- THE ACCOUNT DOORS: the recover popup, and the four answers Shopify
   will not keep. One init because they share a page and both no-op when
   their markup is not on it.

   1. RECOVER AS A POPUP (owner, Pass 3: "Password reset must be a LINK that
      opens a POPUP"). snippets/acct-recover-pop.liquid holds the real
      recover_customer_password form inside a <dialog>; this upgrades the
      link that opens it.

      THE `is-live` CLASS IS THE WHOLE PROGRESSIVE-ENHANCEMENT STORY. A
      <dialog> without [open] is display:none, so a browser with no JS - or
      this browser in the milliseconds before an idle callback runs - would
      have a "Forgot your password?" link that goes nowhere. Both triggers
      keep a real href="#recover", and nav-account.css renders
      `.acct-pop:not(.is-live):target` as an ordinary block in the flow. This
      function claims the dialog by adding the class, and only then does the
      link start opening a modal instead of jumping to an anchor. Nothing is
      ever dead, and the resting page never flashes the inline copy because
      :target does not match until somebody asks for it.

   2. THE PROFILE CLAIM (owner, Pass 3: account creation must also collect a
      scent preference, a birthday, a phone for the courier and separate
      Email / SMS / WhatsApp consent, "tied into the customer profile so LOOM
      analytics can use them").

      Shopify's classic create_customer endpoint accepts six fields and
      SILENTLY DISCARDS customer[phone], customer[note], customer[tags] and
      every customer[metafields][...] - no error, nothing to debug. So these
      answers go to Jennie instead: POST {jennie}/api/profile/claim, keyed on
      the typed email, banked as an UNVERIFIED claim and fused onto the real
      profile only when Shopify's own authenticated customers/* webhook
      arrives with that address. window.theme.customer says verified:false
      about itself, so nothing this browser sends may name a customer.

      NO ENDPOINT, NO CONTROLS. If there is nowhere to send it, the block
      stays hidden rather than collecting four answers into nothing - which
      is exactly the failure Shopify's own form already commits. Same reason
      the block is hidden until this runs. ---- */
function initAcctAuth() {
  /* ---- 1. the recover popup ---- */
  const pop = document.querySelector('[data-recover-pop]');
  if (pop && typeof pop.showModal === 'function') {
    pop.classList.add('is-live');
    const open = () => {
      if (!pop.open) pop.showModal();
      const mail = pop.querySelector('#recover-email');
      if (mail && !mail.value) setTimeout(() => { try { mail.focus(); } catch (e) { /* gone */ } }, 60);
    };
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-recover-open]')) return;
      e.preventDefault();
      open();
    });
    pop.addEventListener('click', (e) => {
      if (e.target.closest('[data-recover-close]')) { e.preventDefault(); pop.close(); return; }
      if (e.target === pop) pop.close();            /* the backdrop */
    });
    /* THE DEEP LINK AND THE ANSWER. Shopify's own emails and every old
       bookmark point at /account/login#recover, and the preview harness
       links ?recover=1. And after a successful post Shopify redirects back
       here with the alert rendered inside the dialog - if it did not reopen,
       "check your inbox" would be invisible and the page would look like it
       had done nothing at all. */
    const asked = location.hash === '#recover' || /[?&]recover=1/.test(location.search);
    if (asked || pop.querySelector('.acct-alert')) open();
  }

  /* ---- 2. the profile claim ---- */
  const box = document.querySelector('[data-acct-prefs]');
  if (!box) return;
  const EP = ((window.theme && window.theme.jennie && window.theme.jennie.endpoint) || '')
    .trim().replace(/\/+$/, '');
  if (!EP) return;
  box.classList.add('is-live');

  const ctx = box.dataset.prefsContext || 'register';
  const said = box.querySelector('[data-prefs-said]');
  const pills = Array.from(box.querySelectorAll('[data-pref-fam]'));
  const leans = Array.from(box.querySelectorAll('[data-pref-lean]'));
  const favBox = box.querySelector('[data-prefs-fav]');
  const favs = Array.from(box.querySelectorAll('[data-pref-fav]'));
  const chk = (k) => box.querySelector('[data-pref-consent="' + k + '"]');
  const val = (sel) => { const el = box.querySelector(sel); return el && el.value ? el.value.trim() : ''; };
  /* The one pressed pill in a single-answer bank, or nothing. */
  const pressed = (list, key) => {
    const hit = list.filter((p) => p.getAttribute('aria-pressed') === 'true')[0];
    return hit ? (hit.dataset[key] || null) : null;
  };

  /* The unguessable id this shop's own server minted into this browser. It is
     the only thing in here that can make a claim land immediately: paired
     with the address, it is what /api/profile/claim treats as somebody
     correcting their own record. Absent for anyone who has never opened the
     chat, which is fine - the claim just waits for Shopify to vouch.

     TWO PLACES, NOT ONE, AND THE SECOND ONE IS THE ONE THAT SURVIVES.
     jennie.js writes the id to localStorage AND to the juce_jid cookie, and
     the same read in assets/juce-loom.js (jennieId, ~line 355) has always
     taken both in that order. This one read only localStorage - which is the
     copy browsers clear on a storage sweep, partition inside an embedded
     context, and drop entirely in a private window that survived a reload.
     The cookie outlives all three.

     That gap was not cosmetic: without an id the save takes the UNTRUSTED
     route, and an untrusted claim may not record a marketing consent for a
     customer who already existed when it was typed - which is every single
     person on the account page. So a shopper who has chatted, and whose
     ownership proof was sitting in this browser the whole time, was having
     their consent silently dropped. Kept as a small local twin of the loom
     one rather than an export: two files reading a browser's own storage do
     not need a module boundary between them, and the pattern (and the reason)
     is written down in both. */
  function jennieId() {
    try {
      const s = JSON.parse(localStorage.getItem('juce:jennie:v1') || '{}') || {};
      if (typeof s.jennie_id === 'string' && s.jennie_id) return s.jennie_id;
    } catch (e) { /* fall through to the cookie */ }
    const m = document.cookie.match(/(?:^|;\s*)juce_jid=([0-9a-f-]{36})/i);
    return m ? m[1] : '';
  }

  const on = (p) => p.getAttribute('aria-pressed') === 'true';
  const press = (p, yes) => p.setAttribute('aria-pressed', yes ? 'true' : 'false');

  /* THE FAVOURITE FOLLOWS THE BANK. It offers exactly the families they have
     chosen and nothing else, it is not on the page until there are two of them
     to choose between, and a favourite whose family has just been un-picked is
     cleared rather than left pointing at something they no longer like. Called
     after every change to either row, and once on load for the account page. */
  function syncFav() {
    if (!favBox) return;
    const want = pills.filter(on).map((p) => p.dataset.prefFam);
    favs.forEach((f) => {
      const offered = want.indexOf(f.dataset.prefFav) > -1;
      f.hidden = !offered;
      if (!offered) press(f, false);
    });
    /* Hidden is not cleared. Narrowing the bank down to one family leaves the
       question with nothing to ask, but it does not un-say the answer - if the
       one thing they like is the one they called their favourite, both are
       true and both are posted. */
    favBox.hidden = want.length < 2;
  }

  pills.forEach((p) => p.addEventListener('click', () => {
    press(p, !on(p));
    syncFav();
  }));

  /* One answer, so pressing one releases the rest and pressing the pressed one
     clears it - a shopper who would rather not say needs no extra button. */
  favs.forEach((f) => f.addEventListener('click', () => {
    const was = on(f);
    favs.forEach((q) => press(q, false));
    press(f, !was);
  }));

  /* WHAT THEY USUALLY WEAR TAKES MORE THAN ONE ANSWER (owner, 2026-08-28:
     "able to select multiple"). It used to behave like the favourite - press
     one and the rest cleared - which made somebody who wears both feminine
     and masculine bottles pick a side they do not have.

     The wire field is still `scent_lean` and one pick still sends exactly the
     string it always sent, so every row already in the table and every reader
     of the column is untouched. Several picks send them comma-joined, in the
     bank's own order, and core/profile-claim.ts validates each member against
     SCENT_LEAN before it stores anything. */
  leans.forEach((p) => p.addEventListener('click', () => press(p, !on(p))));

  function payload(email) {
    return {
      email: email,
      origin: ctx === 'account' ? 'account' : 'register',
      jennie_id: jennieId() || null,
      scent_preference: pills.filter((p) => p.getAttribute('aria-pressed') === 'true')
        .map((p) => p.dataset.prefFam),
      favourite_family: pressed(favs, 'prefFav'),
      scent_lean: leans.filter((p) => p.getAttribute('aria-pressed') === 'true')
        .map((p) => p.dataset.prefLean).join(', ') || null,
      birthday: val('[data-pref-birthday]') || null,
      courier_phone: val('[data-pref-phone]') || null,
      email_marketing: !!(chk('email') && chk('email').checked),
      sms_marketing: !!(chk('sms') && chk('sms').checked),
      whatsapp_marketing: !!(chk('whatsapp') && chk('whatsapp').checked)
    };
  }

  function send(email, keepalive) {
    return fetch(EP + '/api/profile/claim', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload(email)),
      keepalive: !!keepalive
    });
  }

  if (ctx === 'register') {
    /* ---- F1: THE COOKIE ROW, and the three ways it refuses to appear ----

       Owner, twice and explicitly: "On account creation, automatically tick
       email, text, WhatsApp and cookies." The first three are `checked` in
       acct-prefs.liquid and ride the form. Cookies cannot: there is no
       Shopify field and no Jennie field for a cookie grant, and a tick that
       only set its own .checked would write NOTHING - no juce_consent cookie,
       no mirror, no server receipt - while looking exactly like a record. So
       the row is a control that calls the consent gate, and the gate is what
       writes.

       IT RENDERS `hidden` AND ONLY THIS CODE REVEALS IT, because there are
       three states in which a tick here would be a lie, and in all three the
       right answer is not to show the row at all rather than to show a box
       that quietly does nothing:

         no [data-gate] on the page - juce-consent.js returns early without
           it, window.juceConsent stays the inert stub, and accept() answers
           false. Nothing to call.
         a privacy signal - GPC or Do Not Track is an answer given somewhere
           that outranks a tick box on our register form. accept() refuses
           under it, and it is right to; showing the row anyway would be us
           asking a question that has already been answered.
         already decided - if they have used the banner, that answer is
           theirs and is not ours to overwrite from a pre-ticked default.

       WHEN THIS CAN BE ASKED, which is not when this function runs.
       layout/theme.liquid loads theme.js and juce-consent.js as two
       `type="module"` scripts in that order, so both are deferred and both
       execute in document order: this controller is GUARANTEED to run before
       the gate module has executed at all. window.juceConsent is not the
       inert stub at that point - it does not exist. Asking it anything here
       answers "no gate", which deletes the row on every register page. (It
       did, the moment this moved from idle to critical, and that is the one
       thing the move must not cost.)

       So the two halves are split by what each one needs. The submit handler
       below is bound NOW, because a control that is dead when pressed is the
       whole reason this tier exists. The REVEAL waits for DOMContentLoaded,
       which is the deterministic signal: deferred and module scripts all
       execute BEFORE it fires, so by then juce-consent.js has either booted
       or is never going to.

       NOT the juce:consent event, which looks like the obvious hook and is
       the wrong one - measured, not guessed. juce-consent.js calls
       announce() from inside start()'s boot block, and start() assigns the
       real window.juceConsent AFTER that block. So the boot announcement
       arrives while the INERT stub is still on window, reading state
       'unknown', and settling on it removes the row every time. The event's
       detail does carry the true state, but the reveal also has to know
       there is a real accept() to call, and that is exactly the thing not on
       window yet. DOMContentLoaded is later than both and needs to know
       nothing about the other module's internals. */
    const cookieRow = box.querySelector('[data-prefs-cookie]');
    if (cookieRow) {
      const settle = () => {
        const gate = window.juceConsent;
        const usable = !!document.querySelector('[data-gate]')
          && !!gate && typeof gate.accept === 'function'
          && gate.state === 'undecided';
        if (usable) cookieRow.hidden = false;
        else cookieRow.remove();
      };
      /* afterDomReady, not readyState. This file can arrive after
         DOMContentLoaded has fired while readyState still reads
         'interactive', and a listener added then would wait forever. */
      afterDomReady(settle);

      /* AND IF THEY ANSWER THE BANNER WHILE THE FORM IS STILL OPEN. The
         banner sits on this page too, so the sequence "see the register
         form, press essential only, then press Create account" is ordinary,
         not exotic. The submit path already refuses to overwrite that answer
         - twice over - but a ticked box that has quietly stopped doing
         anything is still the form telling them something untrue. So the row
         goes, on the same announcement the rest of the theme listens to.
         detail.state, not window.juceConsent.state, for the reason written
         above: at the boot announcement the real surface is not on window
         yet, and the detail is. */
      document.addEventListener('juce:consent', (ev) => {
        const st = ev && ev.detail && ev.detail.state;
        if (st && st !== 'undecided' && cookieRow.isConnected) cookieRow.remove();
      });
    }

    /* WHY THIS IS A FUNCTION AND NOT A CAPTURED NODE, which is the whole of
       the bug it replaces.

       The first cut held `const cookieBox = chk('cookies')` taken BEFORE the
       block above, and then tested `cookieBox.checked` at submit. Removing
       the row does not change the input: a detached <input> keeps its
       .checked, and the pre-tick from acct-prefs.liquid means that value is
       `true`. So the one path built to protect an existing answer - remove
       the row, do not ask again - was the path that overwrote it. A shopper
       who had pressed "essential only" on the banner ended up with
       analytics + personalisation granted, and a server receipt stamped
       'account', one second after pressing Create account.

       So: nothing is captured. At submit we ask the DOM whether the row is
       still there and still ticked, and we ask the GATE what it thinks the
       answer is RIGHT NOW - because the banner can be answered between this
       page loading and the form being sent, and that answer is newer than
       our default and is theirs. Three separate ways to say no, all live:
       the row is gone, the box is clear, or the gate is no longer
       undecided. */
    const cookieWanted = () => {
      const row = box.querySelector('[data-prefs-cookie]');
      if (!row || !row.isConnected || row.hidden) return false;
      const input = row.querySelector('[data-pref-consent="cookies"]');
      return !!(input && input.checked);
    };

    /* THE NATIVE POST IS NEVER BLOCKED. Shopify's form has to submit whether
       or not we reach Jennie - a shopper who cannot make an account because
       our CRM was down would be a far worse bug than a lost birthday.
       `keepalive` is what lets the request outlive the navigation. */
    const form = box.closest('form');
    if (form) {
      form.addEventListener('submit', () => {
        const field = form.querySelector('[name="customer[email]"]');
        const email = field && field.value ? field.value.trim() : '';
        if (email) { try { send(email, true); } catch (e) { /* the account is what matters */ } }

        /* PRESSING "Create account" WITH THE BOX STILL TICKED IS THE GESTURE.
           Not the page load, and not the default - which is why nothing is
           granted until this handler runs, and why clearing the box before
           submitting grants nothing at all. Ever.

           Only 'analytics' and 'personalisation' are asked for, never
           'marketing'. accept() would drop marketing anyway while the shop
           dial is off, but naming the two keeps that true if the dial is
           ever turned on: advertising cookies are the banner's to grant,
           where there is copy explaining them, and a row on a sign-up form
           is not that copy.

           THE RACE, STATED PLAINLY. decide() awaits a SHA-256 of the gate
           copy before it writes the record, and this browser is about to
           navigate. The receipt itself is keepalive so it survives; the
           local record is written a microtask later and may not. It fails
           CLOSED - no record means no grant and the banner asks them
           normally on the next page, which is the correct failure and not a
           silent yes. */
        if (cookieWanted()) {
          try {
            const gate = window.juceConsent;
            /* The state test is repeated here, live, and it is not
               belt-and-braces: the reveal above ran at page load and this
               runs whenever they press the button. Everything in between -
               the banner being answered in another tab and read back off the
               cookie, a privacy signal arriving with a late extension, the
               module finishing its own boot - lands in that gap. accept()
               refuses all three itself now, so this is the near guard and
               that is the far one; either alone would close the hole, and
               having both means neither caller has to remember. */
            if (gate && typeof gate.accept === 'function'
              && gate.state === 'undecided') {
              gate.accept(['analytics', 'personalisation']);
            }
          } catch (e) { /* the account is what matters */ }
        }
      });
    }
    return;
  }

  /* ---- the account dashboard: read it back, then save it ---- */
  const email = box.dataset.prefsEmail || '';
  const save = box.querySelector('[data-prefs-save]');
  if (!email) return;

  function fill(d) {
    if (!d || !d.known) return;
    const want = Array.isArray(d.scent_preference) ? d.scent_preference : [];
    pills.forEach((p) => p.setAttribute('aria-pressed',
      want.indexOf(p.dataset.prefFam) > -1 ? 'true' : 'false'));
    favs.forEach((f) => press(f, f.dataset.prefFav === d.favourite_family));
    syncFav();
    /* Split on the same comma the payload joins with, so a profile holding
       "Feminine, Gender Neutral" repaints both pills rather than neither. A
       single stored value has no comma and falls out of this as a one-item
       list, which is the old behaviour exactly. */
    const held = String(d.scent_lean || '').split(',').map((v) => v.trim()).filter(Boolean);
    leans.forEach((p) => press(p, held.indexOf(p.dataset.prefLean) > -1));
    const b = box.querySelector('[data-pref-birthday]');
    if (b && d.birthday) b.value = d.birthday;
    const ph = box.querySelector('[data-pref-phone]');
    if (ph && d.courier_phone) ph.value = d.courier_phone;
    [['email', d.email_marketing], ['sms', d.sms_marketing], ['whatsapp', d.whatsapp_marketing]]
      .forEach((pair) => { const c = chk(pair[0]); if (c) c.checked = pair[1] === 'SUBSCRIBED'; });
  }

  /* THE ONLY NETWORK CALL THIS FUNCTION MAKES ON LOAD, and it is the reason
     the whole controller used to sit in idle. It does not need to be: it
     fills fields that are already editable and already correct-by-default,
     and it is behind a disclosure the shopper has to open. So the read waits
     for idle and for the page to be genuinely theirs, exactly as
     initHomeReviews holds its two, while every control above is bound now.
     whenActive because a prerendered account page reading a profile back is
     a visit nobody made. */
  const jid = jennieId();
  if (jid) {
    onIdle(() => whenActive(() => {
      fetch(EP + '/api/profile/claim?jennie_id=' + encodeURIComponent(jid)
        + '&email=' + encodeURIComponent(email))
        .then((r) => (r.ok ? r.json() : null))
        .then(fill)
        .catch(() => { /* an empty panel is still an editable one */ });
    }));
  }

  if (!save) return;

  /* THE WAY FORWARD, NOT A SECOND MECHANISM. data-chat-open is the seam every
     other Jennie door in this theme already carries (header, menu sheet, dock,
     track-parcel, returns), bound once by initJennie at document level - so
     this button is the same door, and it warms the module on hover like the
     rest of them. It is built here rather than sitting in acct-prefs.liquid
     because the state it belongs to is one only the response knows about, and
     a permanently visible "Open Jennie" beside Save would read as an
     instruction to everybody, including the majority for whom the save just
     worked. Cleared with the message: said.textContent = '' takes it away. */
  function chatWay(prompt) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'text-action prefs__said-go';
    b.textContent = 'Open Jennie';
    b.setAttribute('data-chat-open', '');
    b.setAttribute('data-chat-prompt', prompt);
    return b;
  }

  save.addEventListener('click', () => {
    save.disabled = true;
    if (said) { said.textContent = ''; said.classList.remove('is-error'); said.classList.remove('is-warn'); }
    send(email, false)
      .then((r) => r.json().then((d) => ({ status: r.status, d: d })).catch(() => ({ status: r.status, d: null })))
      .then((res) => {
        if (!said) return;
        if (res.status === 429) { said.textContent = 'Too many changes just now. Try again in a minute.'; said.classList.add('is-error'); return; }
        if (res.status === 503) { said.textContent = 'We cannot take these right now.'; said.classList.add('is-error'); return; }
        if (!res.d || res.d.ok !== true) { said.textContent = (res.d && res.d.error) || 'That did not save. Try again in a moment.'; said.classList.add('is-error'); return; }
        /* THREE DIFFERENT TRUE THINGS, AND THEY ARE NOT THE SAME.

           `applied` means it is on the profile now - this browser proved it
           was theirs and the newest word won.

           Without it the answer is banked against this address and lands at
           the next authenticated join, which for a shopper means their next
           order. True of a birthday, a preference and a phone number.

           NOT true of the three marketing boxes, and that is what
           `consent_needs_confirming` says. On the account page the shopper
           already existed when they ticked them, and a claim this server
           cannot tie to them is not allowed to record a consent for somebody
           who already exists - that rule is what stops a stranger forging an
           opt-in by guessing an address, and it costs this shopper their
           answer. No later order changes it. So the page stops promising and
           asks for the one thing that would fix it: a hello to Jennie, which
           is what mints the id that makes the next save trusted. Their
           details are saved either way, and nothing here is their fault. */
        if (res.d.applied) { said.textContent = 'Saved.'; return; }
        if (res.d.consent_needs_confirming) {
          said.textContent = 'Saved, apart from the marketing choices - we can only record those from a browser we recognise. Say hello to Jennie and save again, and they will stick. ';
          said.classList.add('is-warn');
          said.appendChild(chatWay('Hi Jennie. I ticked my email, SMS and WhatsApp choices on my account page and they need confirming.'));
          return;
        }
        said.textContent = 'Saved. It reaches your profile the next time you order.';
      })
      .catch(() => { if (said) { said.textContent = 'That did not save. Try again in a moment.'; said.classList.add('is-error'); } })
      .then(() => { save.disabled = false; });
  });
}

/* "2026-08-24 13:16:56" and "2027-08-24" both arrive from Flits, and neither
   is a format `new Date()` is specified to parse - Safari has historically
   answered Invalid Date for the first. Sliced rather than parsed, so there is
   no clock and no timezone anywhere near a date that is only ever displayed. */
const CREDIT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function creditDate(raw) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(raw || ''));
  if (!m) return '';
  const mon = CREDIT_MONTHS[Number(m[2]) - 1];
  if (!mon) return '';
  return String(Number(m[3])) + ' ' + mon + ' ' + m[1];
}

/* ---- the account page ---- */

function initAcctCredit() {
  const tile = document.querySelector('[data-credit-tile]');
  /* [data-acct-credit] was the page panel and no longer exists - the sheet
     replaced it (owner, 2026-08-26: "it is duplicating info on the page").
     The selector is kept as a fallback so a theme still serving the old
     snippet from Shopify's cache does not lose its history door on the one
     load between a sync and a hard refresh. */
  const panel = document.querySelector('[data-credit-pop]') || document.querySelector('[data-acct-credit]');
  if (!tile && !panel) return;
  if (!creditCfg()) return;
  /* The one network read, held behind idle AND whenActive inside the function
     rather than by the tier it is registered in - the same shape initAcctAuth
     and initAcctReturns use, so a prerendered account page asks Flits
     nothing. */
  onIdle(() => whenActive(() => {
    creditCall('/api/credit').then((res) => {
      if (!res || res.ok !== true) return;      /* silence, never an error */
      paintAcctCredit(res, tile, panel);
    }).catch(() => {});
  }));
}

function paintAcctCredit(res, tile, panel) {
  const have = Math.max(0, Math.round(Number(res.available) || 0));
  const entries = Array.isArray(res.entries) ? res.entries : [];
  /* NOTHING TO SAY, SO SAY NOTHING. A shopper who has never earned credit
     sees no tile and no panel - "Store credit R 0" reads as a locked door
     rather than an empty pocket. Somebody who has spent it all still gets the
     panel, because their history is the reason the balance is zero. */
  if (have <= 0 && !entries.length) return;

  /* THE TILE IS THE WHOLE PAGE-SIDE SURFACE NOW (owner, 2026-08-26: "it is
     duplicating info on the page"). The balance is written once, here, and
     the note under it becomes the door into the sheet - the shape the
     Addresses tile already uses for "Manage". */
  const open = tile && tile.querySelector('[data-credit-hist-open]');
  const note = tile && tile.querySelector('[data-credit-note]');
  if (tile) {
    const fig = tile.querySelector('[data-credit-figure]');
    if (fig) fig.textContent = bagMoney(have);
    if (note) note.textContent = have > 0 ? 'Yours to spend' : 'All spent';
    tile.hidden = false;
  }

  /* THE LEDGER IS A DIALOG. Every row goes in, not twelve: the
     twelve-then-a-button rule existed to stop a long statement burying the
     page, and inside a sheet that scrolls on its own there is no page left to
     bury. */
  const pop = panel && panel.matches('[data-credit-pop]')
    ? panel
    : document.querySelector('[data-credit-pop]');
  if (!pop) return;

  const big = pop.querySelector('[data-credit-figure-big]');
  if (big) big.textContent = bagMoney(have);
  const earned = pop.querySelector('[data-credit-earned]');
  if (earned) earned.textContent = bagMoney(res.earned);
  const spent = pop.querySelector('[data-credit-spent]');
  if (spent) spent.textContent = bagMoney(res.spent);

  const log = pop.querySelector('[data-credit-log]');
  if (!open || !log || !entries.length) return;

  log.replaceChildren(...entries.map(creditRow));
  /* The note stops being prose and becomes the door only now - an account
     with a balance and no rows keeps "Yours to spend" and offers no way into
     an empty room. */
  if (note) note.hidden = true;
  open.hidden = false;
  /* .is-live tells the CSS a controller is here, the same signal .acct-pop
     already reads to decide whether its close button is real. */
  pop.classList.add('is-live');
  open.addEventListener('click', () => {
    if (typeof pop.showModal === 'function') pop.showModal();
    else pop.setAttribute('open', '');
  });
  const shut = () => { if (pop.open) pop.close(); };
  const closeBtn = pop.querySelector('[data-credit-pop-close]');
  if (closeBtn) closeBtn.addEventListener('click', shut);
  /* Click the backdrop to leave. A <dialog>'s backdrop is not a child, so the
     press lands on the dialog itself - anything inside .acct-pop__shell stops
     short of here. */
  pop.addEventListener('click', (e) => { if (e.target === pop) shut(); });
}

function creditRow(e) {
  const li = document.createElement('li');
  li.className = 'acct-credit__row' + (Number(e.cents) < 0 ? ' is-out' : ' is-in');

  const why = document.createElement('span');
  why.className = 'acct-credit__why';
  /* textContent, not innerHTML: this string is typed by whoever adjusted the
     balance, in an admin this theme does not own. */
  why.textContent = e.reason || 'Adjustment';

  const amt = document.createElement('span');
  amt.className = 'acct-credit__amt price';
  const n = Math.round(Number(e.cents) || 0);
  amt.textContent = (n > 0 ? '+' : '') + creditMoney(n);

  li.append(why, amt);

  /* THE META, ON ONE LINE (owner, 2026-08-26: "improve the display of the how
     it moved"). Date, balance and expiry used to be three separate grid areas,
     which on a 34rem sheet gave every entry four ragged lines: a reason
     wrapping to two, then "Expires" alone on the left and "Balance" alone on
     the right, reading as two unrelated captions rather than as the small
     print of one row.

     They are the same kind of fact - context for the amount - so they are one
     quiet line now, separated by middots. The reason keeps the top line to
     itself, which is what stopped it wrapping. */
  const bits = [creditDate(e.at)];
  if (e.balanceCents !== null && e.balanceCents !== undefined) {
    bits.push('Balance ' + bagMoney(e.balanceCents));
  }
  /* Credit that expires and credit that does not are different things to own,
     and only the row knows which it is. */
  if (e.expires && n > 0) bits.push('Expires ' + creditDate(e.expires));

  const meta = document.createElement('span');
  meta.className = 'acct-credit__meta';
  meta.textContent = bits.filter(Boolean).join(' · ');
  li.append(meta);
  return li;
}
